/**
 * Column keys and preset categories are closed proto enum integer values; the maps below must
 * mirror the wire integers so the legacy string keys round-trip.
 */

import { timestampDate } from '@bufbuild/protobuf/wkt';

const COLUMN_KEYS = {
  1: 'shot_number',
  2: 'thumbnail',
  3: 'scene',
  4: 'location',
  5: 'description',
  6: 'cast',
  7: 'frame_size',
  8: 'angle',
  9: 'camera_movement',
  10: 'equipment',
  11: 'props',
  12: 'director_notes',
  13: 'dp_notes',
  14: 'vfx_notes',
  15: 'sfx_notes',
  16: 'dialogue',
  17: 'shooting_time',
};

const COLUMN_ENUMS = Object.fromEntries(
  Object.entries(COLUMN_KEYS).map(([value, key]) => [key, Number(value)]),
);

const PRESET_CATEGORIES = { 1: 'frameSize', 2: 'angle', 3: 'cameraMovement', 4: 'equipment' };
const PRESET_CATEGORY_ENUMS = { frameSize: 1, angle: 2, cameraMovement: 3, equipment: 4 };

function toIso(timestamp) {
  return timestamp ? timestampDate(timestamp).toISOString() : null;
}

export function shotToLegacy(shot) {
  return {
    id: shot.shotId,
    project_id: shot.projectId,
    image_id: shot.imageId || null,
    sort_order: shot.sortOrder,
    shot_number: shot.shotNumber,
    scene: shot.scene,
    location: shot.location,
    description: shot.description,
    cast_ids: shot.castIds ?? [],
    frame_size: shot.frameSize,
    angle: shot.angle,
    camera_movement: shot.cameraMovement,
    equipment: shot.equipment ?? [],
    props: shot.props ?? [],
    director_notes: shot.directorNotes,
    dp_notes: shot.dpNotes,
    vfx_notes: shot.vfxNotes,
    sfx_notes: shot.sfxNotes,
    dialogue: shot.dialogue,
    shooting_time: shot.shootingTime,
    created_at: toIso(shot.createdAt),
    updated_at: toIso(shot.updatedAt),
  };
}

export function castToLegacy(cast) {
  return {
    id: cast.castId,
    name: cast.name,
    color: cast.color,
    created_at: toIso(cast.createdAt),
    updated_at: toIso(cast.updatedAt),
  };
}

export function propToLegacy(prop) {
  return {
    id: prop.propId,
    name: prop.name,
    created_at: toIso(prop.createdAt),
    updated_at: toIso(prop.updatedAt),
  };
}

export function presetToLegacy(category, preset) {
  return {
    category: PRESET_CATEGORIES[category] || null,
    value: preset.value,
    label: preset.label,
    custom: preset.custom,
  };
}

export function presetBanksToLegacy(banks) {
  const map = (entries) =>
    (entries ?? []).map((preset) => ({
      value: preset.value,
      label: preset.label,
      custom: preset.custom,
    }));

  return {
    frameSize: map(banks?.frameSize),
    angle: map(banks?.angle),
    cameraMovement: map(banks?.cameraMovement),
    equipment: map(banks?.equipment),
  };
}

export function configToLegacy(config) {
  const keys = (columns) => (columns ?? []).map((column) => COLUMN_KEYS[column]).filter(Boolean);

  return {
    visible_columns: keys(config?.visibleColumns),
    column_order: keys(config?.columnOrder),
  };
}

export function columnsToEnums(keys) {
  return (keys ?? []).map((key) => COLUMN_ENUMS[key]).filter(Boolean);
}

export function presetCategoryToEnum(category) {
  return PRESET_CATEGORY_ENUMS[category] || 0;
}
